/**
 * Retry Logic Utilities
 *
 * Implements exponential backoff with jitter for step retries.
 *
 * @packageDocumentation
 */

/**
 * Calculates the delay for a retry attempt using exponential backoff with jitter.
 *
 * Formula: delay = baseDelay * (2 ^ attempt) * (1 ± jitter)
 *
 * @param attempt - The attempt number (0-indexed)
 * @param baseDelay - Base delay in milliseconds (default: 1000ms)
 * @param jitterFactor - Jitter factor between 0 and 1 (default: 0.2 for ±20%)
 * @returns Delay in milliseconds
 *
 * @example
 * ```typescript
 * calculateBackoff(0, 1000); // ~1000ms
 * calculateBackoff(1, 1000); // ~2000ms ± 20%
 * calculateBackoff(2, 1000); // ~4000ms ± 20%
 * calculateBackoff(3, 1000); // ~8000ms ± 20%
 * ```
 */
export function calculateBackoff(
  attempt: number,
  baseDelay: number = 1000,
  jitterFactor: number = 0.2
): number {
  // Exponential backoff: baseDelay * 2^attempt
  const exponentialDelay = baseDelay * Math.pow(2, attempt);

  // Add jitter: ±jitterFactor
  const jitter = 1 + (Math.random() * 2 - 1) * jitterFactor;

  return Math.floor(exponentialDelay * jitter);
}

/**
 * Sleeps for a specified duration.
 *
 * @param ms - Duration in milliseconds
 * @returns Promise that resolves after the duration
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Checks if an error is recoverable and should be retried.
 *
 * @param error - Error from adapter execution
 * @returns True if error is recoverable
 *
 * @remarks
 * Recoverable errors include:
 * - Network timeouts
 * - Rate limits
 * - Temporary server errors (5xx)
 *
 * Non-recoverable errors include:
 * - Authentication failures
 * - Invalid requests (4xx except 429)
 * - Validation errors
 */
export function isRecoverableError(error: any): boolean {
  if (!error) {
    return false;
  }

  // Check error type
  const errorType = error.type || error.error?.type;

  // Rate limits are recoverable
  if (errorType === 'RateLimitError' || errorType === 'rate_limit_exceeded') {
    return true;
  }

  // Timeouts are recoverable
  if (errorType === 'TimeoutError' || errorType === 'timeout') {
    return true;
  }

  // Network errors are recoverable
  if (errorType === 'NetworkError' || errorType === 'ECONNRESET' || errorType === 'ETIMEDOUT') {
    return true;
  }

  // Server errors (5xx) are recoverable
  if (error.statusCode >= 500 && error.statusCode < 600) {
    return true;
  }

  // Check message content
  const message = (error.message || '').toLowerCase();
  if (message.includes('timeout') || message.includes('rate limit') || message.includes('503')) {
    return true;
  }

  // Default to non-recoverable
  return false;
}
